import { localizarPontoNoMundo, pontosDeAncoragem } from './ancoragem'
import { apoioDaFita, ladoDaPerna, pernasNaEscada } from './fitaDeAncoragem'
import {
  CARGA_MAXIMA_ANCORAGEM_KGF,
  CARGA_MINIMA_ANCORAGEM_KGF,
  CARGA_PADRAO_ANCORAGEM_KGF,
  DESVIO_MAXIMO_CARGA_GRAUS,
} from './configuracaoAvisos'
import { gerarIdPadrao } from './operacoes'
import type {
  AncoragemTabletop,
  CenaTabletop,
  EqualizacaoAncoragem,
  GeradorIdTabletop,
  ReferenciaPonto,
  TipoAncoragem,
  VetorTabletop,
} from './tipos'

/**
 * Ancoragens em V, W e M: criar, editar, apagar e calcular ângulo e carga.
 *
 * O cálculo é DIDÁTICO e idealizado: as pernas se equalizam (todas com a mesma
 * tensão, sem atrito) e a carga se alinha à resultante delas. Com tensão T em
 * cada perna e `u` o vetor unitário da perna, o equilíbrio dá
 * `T · |Σu| = carga`, ou seja, a fração da carga em cada perna é `1 / |Σu|`.
 * No V simétrico isso vale `1 / (2·cos(θ/2))`: 71% com 90°, 100% com 120°.
 * Isso é a ancoragem AUTOEQUALIZÁVEL (X deslizante).
 *
 * Na PRÉ-EQUALIZADA as pernas têm comprimento fixo, amarradas num nó no ponto
 * central. A carga puxa o ponto central na direção dela (vertical, ou desviada
 * de `desvioCargaGraus`) e cada perna estica como uma mola de rigidez
 * proporcional a 1 / comprimento (o mesmo material): a perna mais curta e a
 * mais alinhada com a carga levam mais. Sem desvio, a carga puxa no eixo da
 * ancoragem (o oposto da soma das pernas); o desvio a gira para um lado. Perna que encurtaria fica frouxa e sai
 * da conta. No V isso é só a estática (duas pernas, duas incógnitas no plano).
 *
 * Não substitui o cálculo de engenharia nem a capacidade dos equipamentos.
 *
 * TypeScript puro: sem React e sem Three.js.
 */

export const PONTOS_DE_FIXACAO: Readonly<Record<TipoAncoragem, number>> = { V: 2, W: 3, M: 4 }
export const TIPOS_ANCORAGEM: readonly TipoAncoragem[] = ['V', 'W', 'M']

/** Abaixo disto a resultante das pernas é quase nula (pernas opostas) e a carga por perna dispara. */
const RESULTANTE_MINIMA = 0.05

export type NovaAncoragem = Readonly<{
  tipo: TipoAncoragem
  fixacoes: readonly ReferenciaPonto[]
  centro: ReferenciaPonto
  cargaKgf?: number
  rotulo?: string
}>

export type AtualizacoesAncoragem = Readonly<{
  cargaKgf?: number
  equalizacao?: EqualizacaoAncoragem
  /** Graus, de -`DESVIO_MAXIMO_CARGA_GRAUS` a +; 0 tira o campo. */
  desvioCargaGraus?: number
  /** Vazio (ou só espaços) tira o rótulo. */
  rotulo?: string
}>

const mesma = (a: ReferenciaPonto, b: ReferenciaPonto) => a.idObjeto === b.idObjeto && a.idPonto === b.idPonto
const copiar = (p: ReferenciaPonto): ReferenciaPonto => ({ idObjeto: p.idObjeto, idPonto: p.idPonto })
const prender = (valor: number, min: number, max: number) => Math.min(max, Math.max(min, valor))

export const limitarCarga = (cargaKgf: number): number =>
  Number.isFinite(cargaKgf)
    ? prender(cargaKgf, CARGA_MINIMA_ANCORAGEM_KGF, CARGA_MAXIMA_ANCORAGEM_KGF)
    : CARGA_PADRAO_ANCORAGEM_KGF

function validar(cena: CenaTabletop, nova: NovaAncoragem): void {
  const esperadas = PONTOS_DE_FIXACAO[nova.tipo]
  if (nova.fixacoes.length !== esperadas) {
    throw new Error(`A ancoragem em ${nova.tipo} tem ${esperadas} pontos de fixação; foram escolhidos ${nova.fixacoes.length}.`)
  }
  const todos = [...nova.fixacoes, nova.centro]
  for (const referencia of todos) {
    const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
    if (!objeto) throw new Error(`A ancoragem usa o objeto "${referencia.idObjeto}", que não existe na cena.`)
    if (!pontosDeAncoragem(objeto).some((p) => p.id === referencia.idPonto)) {
      throw new Error(`O objeto "${objeto.nome}" não tem o ponto de ancoragem "${referencia.idPonto}".`)
    }
  }
  nova.fixacoes.forEach((p, i) => {
    if (nova.fixacoes.slice(0, i).some((outro) => mesma(outro, p))) throw new Error('Um ponto de fixação não pode se repetir.')
  })
  if (nova.fixacoes.some((p) => mesma(p, nova.centro))) throw new Error('O ponto central não pode ser também um ponto de fixação.')
}

export function adicionarAncoragem(cena: CenaTabletop, nova: NovaAncoragem, gerarId: GeradorIdTabletop = gerarIdPadrao): CenaTabletop {
  validar(cena, nova)
  const id = gerarId()
  if ((cena.ancoragens ?? []).some((a) => a.id === id)) throw new Error(`Já existe uma ancoragem com o ID "${id}".`)
  const rotulo = nova.rotulo?.trim()
  const ancoragem: AncoragemTabletop = {
    id,
    tipo: nova.tipo,
    fixacoes: nova.fixacoes.map(copiar),
    centro: copiar(nova.centro),
    cargaKgf: limitarCarga(nova.cargaKgf ?? CARGA_PADRAO_ANCORAGEM_KGF),
    ...(rotulo ? { rotulo } : {}),
  }
  return { ...cena, ancoragens: [...(cena.ancoragens ?? []), ancoragem] }
}

export function atualizarAncoragem(cena: CenaTabletop, id: string, alteracoes: AtualizacoesAncoragem): CenaTabletop {
  if (!cena.ancoragens?.some((a) => a.id === id)) return cena
  return {
    ...cena,
    ancoragens: cena.ancoragens.map((a) => {
      if (a.id !== id) return a
      const { rotulo: _, equalizacao: _e, desvioCargaGraus: _d, ...resto } = a
      const rotulo = alteracoes.rotulo === undefined ? a.rotulo : alteracoes.rotulo.trim()
      const equalizacao = alteracoes.equalizacao ?? a.equalizacao
      const desvio =
        alteracoes.desvioCargaGraus === undefined
          ? a.desvioCargaGraus
          : Number.isFinite(alteracoes.desvioCargaGraus)
            ? prender(alteracoes.desvioCargaGraus, -DESVIO_MAXIMO_CARGA_GRAUS, DESVIO_MAXIMO_CARGA_GRAUS)
            : 0
      return {
        ...resto,
        cargaKgf: alteracoes.cargaKgf === undefined ? a.cargaKgf : limitarCarga(alteracoes.cargaKgf),
        ...(rotulo ? { rotulo } : {}),
        // autoequalizável é o padrão: só a pré-equalizada fica gravada
        ...(equalizacao === 'pre-equalizada' ? { equalizacao } : {}),
        ...(desvio ? { desvioCargaGraus: desvio } : {}),
      }
    }),
  }
}

export function removerAncoragem(cena: CenaTabletop, id: string): CenaTabletop {
  if (!cena.ancoragens?.some((a) => a.id === id)) return cena
  return { ...cena, ancoragens: cena.ancoragens.filter((a) => a.id !== id) }
}

export const ancoragensDoObjeto = (cena: CenaTabletop, idObjeto: string): readonly AncoragemTabletop[] =>
  (cena.ancoragens ?? []).filter((a) => a.centro.idObjeto === idObjeto || a.fixacoes.some((p) => p.idObjeto === idObjeto))

export interface PernaAncoragem {
  readonly referencia: ReferenciaPonto
  /** `null` se o ponto não existe mais. */
  readonly mundo: VetorTabletop | null
  readonly comprimentoM: number | null
}

export interface ResultadoAncoragem {
  readonly centro: VetorTabletop | null
  readonly pernas: readonly PernaAncoragem[]
  /** Algum ponto não existe mais (o objeto mudou de forma): nada é calculado. */
  readonly incompleta: boolean
  /** Maior ângulo entre duas pernas, em graus; `null` se incompleta. */
  readonly anguloMaximoGraus: number | null
  /** Pares de pernas (índices) e o ângulo entre elas, em graus. */
  readonly angulosEntrePernas: ReadonlyArray<{ readonly de: number; readonly para: number; readonly graus: number }>
  /** Pernas quase opostas: a resultante é nula e a carga por perna não tem valor finito. */
  readonly degenerada: boolean
  readonly equalizacao: EqualizacaoAncoragem
  /**
   * Fração da carga na perna MAIS carregada: na autoequalizável todas levam o
   * mesmo; na pré-equalizada, a maior. `null` se incompleta ou degenerada.
   */
  readonly fracaoPorPerna: number | null
  readonly cargaPorPernaKgf: number | null
  /** Fração da carga em cada perna, na ordem das fixações; `null` se incompleta ou degenerada. */
  readonly fracoesPorPerna: readonly number[] | null
  readonly cargasPorPernaKgf: readonly number[] | null
  /**
   * Pré-equalizada: a carga sai do plano das pernas (ou nenhuma perna a segura
   * nessa direção) e o ponto central balança até se alinhar. Na
   * autoequalizável o ponto central corre e se alinha sozinho: sempre `false`.
   */
  readonly cargaDesalinhada: boolean
  /** O que acontece se cada ponto de fixação falhar: a fração nas pernas que sobram (a que falhou fica com 0). */
  readonly seUmPontoFalhar: ReadonlyArray<{
    readonly perna: number
    /** `null`: nenhuma perna sobra que segure a carga. */
    readonly fracoes: readonly number[] | null
    /** A carga desce até as pernas que sobram esticarem (a fita corre no mosquetão). */
    readonly comExtensao: boolean
    /**
     * Pré-equalizada: as pernas que sobram não seguram a carga na direção dela
     * e o ponto central balança até se alinhar; `fracoes` já é depois do balanço.
     */
    readonly balanca: boolean
  }>
}

const graus = (radianos: number) => (radianos * 180) / Math.PI

type V3 = readonly [number, number, number]

/** Resolve a·x = b (3 × 3) por eliminação de Gauss com pivô; `null` se singular. */
function resolver3x3(a: number[][], b: number[]): number[] | null {
  const m = a.map((linha, i) => [...linha, b[i]!])
  for (let c = 0; c < 3; c += 1) {
    let pivo = c
    for (let l = c + 1; l < 3; l += 1) if (Math.abs(m[l]![c]!) > Math.abs(m[pivo]![c]!)) pivo = l
    if (Math.abs(m[pivo]![c]!) < 1e-14) return null
    ;[m[c], m[pivo]] = [m[pivo]!, m[c]!]
    for (let l = 0; l < 3; l += 1) {
      if (l === c) continue
      const f = m[l]![c]! / m[c]![c]!
      for (let k = c; k < 4; k += 1) m[l]![k]! -= f * m[c]![k]!
    }
  }
  return [0, 1, 2].map((i) => m[i]![3]! / m[i]![i]!)
}

/**
 * Pré-equalizada: a tensão de cada perna (fração da carga) com o ponto central
 * preso pelas pernas como molas de rigidez 1/L, puxado pela carga na direção
 * `w` (unitário). Pernas que ficariam comprimidas saem e a conta se refaz.
 * `ativas` diz quais pernas existem (as que falharam ficam de fora).
 */
function repartirPreEqualizada(
  unitarios: readonly V3[],
  comprimentos: readonly number[],
  w: V3,
  ativas: readonly boolean[] = unitarios.map(() => true),
): { fracoes: number[]; desalinhada: boolean } | null {
  const ligadas = [...ativas]
  for (let volta = 0; volta <= unitarios.length; volta += 1) {
    const k = unitarios.map((_, i) => (ligadas[i] ? 1 / comprimentos[i]! : 0))
    const K = [0, 1, 2].map((r) => [0, 1, 2].map((c) => unitarios.reduce((s, u, i) => s + k[i]! * u[r]! * u[c]!, 0)))
    const traco = K[0]![0]! + K[1]![1]! + K[2]![2]!
    if (traco <= 0) return null
    // regularização mínima: a carga fora do plano das pernas não estica nenhuma delas
    const eps = traco * 1e-9
    const delta = resolver3x3(
      K.map((linha, r) => linha.map((v, c) => (r === c ? v + eps : v))),
      [w[0], w[1], w[2]],
    )
    if (!delta) return null
    const tensoes = unitarios.map((u, i) => (ligadas[i] ? -k[i]! * (u[0] * delta[0]! + u[1] * delta[1]! + u[2] * delta[2]!) : 0))
    const comprimida = tensoes.findIndex((t, i) => ligadas[i] && t < -1e-9)
    if (comprimida >= 0) {
      ligadas[comprimida] = false
      continue
    }
    const fracoes = tensoes.map((t) => Math.max(0, t))
    // o que sobra da carga sem perna para segurar: o ponto central balança
    const segurado = unitarios.reduce<[number, number, number]>(
      (s, u, i) => [s[0] + fracoes[i]! * u[0], s[1] + fracoes[i]! * u[1], s[2] + fracoes[i]! * u[2]],
      [0, 0, 0],
    )
    const sobra = Math.hypot(segurado[0] + w[0], segurado[1] + w[1], segurado[2] + w[2])
    if (fracoes.every((f) => f < 1e-9)) return null
    return { fracoes, desalinhada: sobra > 0.05 }
  }
  return null
}

/** Autoequalizável: a mesma tensão nas pernas ativas, `1 / |Σu|` de cada uma; `null` se as pernas se anulam. */
function repartirAutoequalizavel(unitarios: readonly V3[], ativas: readonly boolean[] = unitarios.map(() => true)): number[] | null {
  const soma = unitarios.reduce<[number, number, number]>(
    (s, u, i) => (ativas[i] ? [s[0] + u[0], s[1] + u[1], s[2] + u[2]] : s),
    [0, 0, 0],
  )
  const resultante = Math.hypot(soma[0], soma[1], soma[2])
  if (resultante < RESULTANTE_MINIMA) return null
  return unitarios.map((_, i) => (ativas[i] ? 1 / resultante : 0))
}

/**
 * A direção da carga. Sem desvio, é o eixo da ancoragem: o oposto da soma das
 * pernas (para baixo numa ancoragem pendurada, para o lado do vão numa
 * ancoragem em árvores). O desvio gira essa direção `desvio` graus no sentido
 * da primeira para a última fixação.
 */
function direcaoDaCarga(unitarios: readonly V3[], fixacoes: readonly VetorTabletop[], desvioGraus: number): V3 {
  const soma = unitarios.reduce<[number, number, number]>((s, u) => [s[0] + u[0], s[1] + u[1], s[2] + u[2]], [0, 0, 0])
  const n = Math.hypot(soma[0], soma[1], soma[2])
  const eixo: V3 = n > 1e-9 ? [-soma[0] / n, -soma[1] / n, -soma[2] / n] : [0, -1, 0]
  const a = fixacoes[0]!
  const b = fixacoes[fixacoes.length - 1]!
  // o lado: da primeira para a última fixação, sem a parte ao longo do eixo
  const ab: V3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]]
  const ao = ab[0] * eixo[0] + ab[1] * eixo[1] + ab[2] * eixo[2]
  const ladoBruto: V3 = [ab[0] - ao * eixo[0], ab[1] - ao * eixo[1], ab[2] - ao * eixo[2]]
  const m = Math.hypot(ladoBruto[0], ladoBruto[1], ladoBruto[2])
  const lado: V3 = m > 1e-9 ? [ladoBruto[0] / m, ladoBruto[1] / m, ladoBruto[2] / m] : [0, 0, 0]
  const r = (desvioGraus * Math.PI) / 180
  return [
    eixo[0] * Math.cos(r) + lado[0] * Math.sin(r),
    eixo[1] * Math.cos(r) + lado[1] * Math.sin(r),
    eixo[2] * Math.cos(r) + lado[2] * Math.sin(r),
  ]
}

export function calcularAncoragem(cena: CenaTabletop, ancoragem: AncoragemTabletop): ResultadoAncoragem {
  const centro = localizarPontoNoMundo(cena, ancoragem.centro)
  // na escada, as pernas ficam na mesma altura, uma em cada banzo
  const naEscada = pernasNaEscada(cena, ancoragem.fixacoes)
  const pernas: PernaAncoragem[] = ancoragem.fixacoes.map((referencia, i) => {
    // a perna sai de onde o anel abraça a estrutura: na escada, o banzo, nunca o meio do degrau
    const mundo = apoioDaFita(cena, referencia, centro, ladoDaPerna(i), 1, naEscada[i])?.ponto ?? null
    const comprimentoM = mundo && centro ? Math.hypot(mundo[0] - centro[0], mundo[1] - centro[1], mundo[2] - centro[2]) : null
    return { referencia, mundo, comprimentoM }
  })
  const vazio: ResultadoAncoragem = {
    centro,
    pernas,
    incompleta: true,
    anguloMaximoGraus: null,
    angulosEntrePernas: [],
    degenerada: false,
    equalizacao: ancoragem.equalizacao ?? 'autoequalizavel',
    fracaoPorPerna: null,
    cargaPorPernaKgf: null,
    fracoesPorPerna: null,
    cargasPorPernaKgf: null,
    cargaDesalinhada: false,
    seUmPontoFalhar: [],
  }
  if (!centro || pernas.some((p) => !p.mundo || !p.comprimentoM)) return vazio

  const unitarios = pernas.map((p) => {
    const m = p.mundo!
    const c = p.comprimentoM!
    return [(m[0] - centro[0]) / c, (m[1] - centro[1]) / c, (m[2] - centro[2]) / c] as const
  })
  const angulos: Array<{ de: number; para: number; graus: number }> = []
  for (let i = 0; i < unitarios.length; i += 1) {
    for (let j = i + 1; j < unitarios.length; j += 1) {
      const a = unitarios[i]!
      const b = unitarios[j]!
      const cos = prender(a[0] * b[0] + a[1] * b[1] + a[2] * b[2], -1, 1)
      angulos.push({ de: i, para: j, graus: graus(Math.acos(cos)) })
    }
  }
  const equalizacao = ancoragem.equalizacao ?? 'autoequalizavel'
  const comprimentos = pernas.map((p) => p.comprimentoM!)
  const w = direcaoDaCarga(
    unitarios,
    pernas.map((p) => p.mundo!),
    ancoragem.desvioCargaGraus ?? 0,
  )
  const repartir = (ativas?: readonly boolean[]) => {
    if (equalizacao === 'autoequalizavel') {
      const f = repartirAutoequalizavel(unitarios, ativas)
      return f ? { fracoes: f, desalinhada: false } : null
    }
    return repartirPreEqualizada(unitarios, comprimentos, w, ativas)
  }
  const reparticao = repartir()
  // as pernas quase opostas valem para os dois tipos: a carga por perna dispara
  const degenerada = repartirAutoequalizavel(unitarios) === null || reparticao === null
  const fracoes = degenerada ? null : reparticao!.fracoes
  const maior = fracoes ? Math.max(...fracoes) : null
  return {
    centro,
    pernas,
    incompleta: false,
    anguloMaximoGraus: Math.max(...angulos.map((a) => a.graus)),
    angulosEntrePernas: angulos,
    degenerada,
    equalizacao,
    fracaoPorPerna: maior,
    cargaPorPernaKgf: maior === null ? null : maior * ancoragem.cargaKgf,
    fracoesPorPerna: fracoes,
    cargasPorPernaKgf: fracoes ? fracoes.map((f) => f * ancoragem.cargaKgf) : null,
    cargaDesalinhada: !degenerada && reparticao!.desalinhada,
    seUmPontoFalhar: degenerada
      ? []
      : pernas.map((_, perna) => {
          const ativas = pernas.map((__, i) => i !== perna)
          const r = repartir(ativas)
          if (r && r.desalinhada) {
            // o ponto central balança até a carga se alinhar com as pernas que sobram
            return { perna, fracoes: repartirAutoequalizavel(unitarios, ativas), comExtensao: false, balanca: true }
          }
          return { perna, fracoes: r ? r.fracoes : null, comExtensao: equalizacao === 'autoequalizavel', balanca: false }
        }),
  }
}
